import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit"
import * as service from './service'
import { RootState } from "../../../redux/store"
import { AnnualReport, QuaterlyReport } from './types'

const SLICE_PREFIX = 'reports'
export type LoadingState = 'not-asked' | 'loading' | 'done' | 'error'

interface State {
  customerNumber: string | null
  certificateCount: number | null
  biologicalCertificate: Blob | null
  annualReports: AnnualReport[] | null
  quarterlyReports: QuaterlyReport[] | null
  loadingState: {
    certificates: LoadingState,
    quarterlyReports: LoadingState,
    annualReports: LoadingState,
  },
}

const initialState: State = {
  certificateCount: null,
  customerNumber: null,
  biologicalCertificate: null,
  annualReports: null,
  quarterlyReports: null,
  loadingState: {
    certificates: 'not-asked',
    quarterlyReports: 'not-asked',
    annualReports: 'not-asked',
  },
}

export const getBiologicalCertificateCount = createAsyncThunk(
  `${SLICE_PREFIX}/getBiologicalCertificateCount`,
  async (customerNumber: string) => {
    return await service.getBiologicalCertificateCount(customerNumber)
  }
)

export const getCertificatesData = createAsyncThunk(`${SLICE_PREFIX}/getCertificatesData`, async (customerNumber: string, { dispatch }) => {
  await dispatch(getBiologicalCertificateCount(customerNumber))

  return customerNumber
})

export const getCertificate = createAsyncThunk<
  Blob,
  { customer_number: string; doctor_name: string }
>(
  `${SLICE_PREFIX}/getCertificate`,
  async (customerDetails) => {
    const data = await service.getBiologicalCertificate(customerDetails.customer_number, customerDetails.doctor_name)
    return data
  }
)

export const getQuarterlyReports = createAsyncThunk(
  `${SLICE_PREFIX}/getQuarterlyReports`,
  async (customerNumber: string) => {
    const reports: QuaterlyReport[] =  await service.getQuarterlyReports(customerNumber)
    return reports.sort((a, b) => {
      return (b.year - a.year) || (b.quarter - a.quarter)
    })
  }
)

export const getAnnualReports = createAsyncThunk(
  `${SLICE_PREFIX}/getAnnualReports`,
  async (customerNumber: string) => {
    return await service.getAnnualReports(customerNumber)
  }
)

export const shipmentsSlice = createSlice({
  name: SLICE_PREFIX,
  initialState,
  reducers: {
    clearReportsData: () => {
      return initialState
    }
  },
  extraReducers(builder) {
    builder.addCase(getBiologicalCertificateCount.fulfilled, (state, { payload }) => {
      state.certificateCount = payload
    })

    builder.addCase(getCertificate.fulfilled, (state, { payload }) => {
      state.biologicalCertificate = payload
    })

    builder.addCase(getQuarterlyReports.fulfilled, (state, { payload }) => {
      state.quarterlyReports = payload
    })

    builder.addCase(getAnnualReports.fulfilled, (state, { payload }) => {
      state.annualReports = payload
    })

    builder.addMatcher(isPending(getCertificatesData), (state) => {
      state.loadingState.certificates = 'loading'
    })

    builder.addMatcher(isRejected(getCertificatesData), (state) => {
      state.loadingState.certificates = 'error'
    })

    builder.addMatcher(isFulfilled(getCertificatesData), (state, action) => {
      state.customerNumber = action.payload
      state.loadingState.certificates = 'done'
    })

    builder.addMatcher(isPending(getQuarterlyReports), (state) => {
      state.loadingState.quarterlyReports = 'loading'
    })

    builder.addMatcher(isRejected(getQuarterlyReports), (state) => {
      state.loadingState.quarterlyReports = 'error'
    })

    builder.addMatcher(isFulfilled(getQuarterlyReports), (state) => {
      state.loadingState.quarterlyReports = 'done'
    })

    builder.addMatcher(isPending(getAnnualReports), (state) => {
      state.loadingState.annualReports = 'loading'
    })

    builder.addMatcher(isRejected(getAnnualReports), (state) => {
      state.loadingState.annualReports = 'error'
    })

    builder.addMatcher(isFulfilled(getAnnualReports), (state) => {
      state.loadingState.annualReports = 'done'
    })        
  }
})

export const { clearReportsData } = shipmentsSlice.actions

export const selectCustomerNumber = (state: RootState) => state.reports.customerNumber
export const selectCertificateCount = (state: RootState) => state.reports.certificateCount
export const selectLoadingState = (state: RootState) => state.reports.loadingState
export const selectAnnualReports = (state: RootState) => state.reports.annualReports
export const selectQuarterlyReports = (state: RootState) => state.reports.quarterlyReports

export default shipmentsSlice.reducer